import React, { useEffect, useState } from "react";
import { Loader2, Globe, Server, ShoppingBag, ExternalLink, Lock, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DomainSearch from "./DomainSearch";

/**
 * Publish flow modal — three destinations:
 *  (a) Optivize subdomain {slug}.optivize.digital  (cheapest)
 *  (b) Your own custom domain                       (need DNS)
 *  (c) Buy a domain from Optivize                   (one-stop)
 *
 * Backend gate: /api/sites/can-publish must return allowed=true (active AI Builder sub).
 */
export default function PublishSiteModal({ aiJobId, defaultSlug = "", onClose, onPublished }) {
  const nav = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [dest, setDest] = useState("subdomain");
  const [slug, setSlug] = useState(defaultSlug);
  const [customDomain, setCustomDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/sites/can-publish").then(r => setAllowed(r.data.allowed)).catch(() => setAllowed(false));
  }, []);

  const publish = async () => {
    if (dest === "custom_domain" && !customDomain.match(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/)) {
      toast.error("Enter a valid custom domain (e.g. yourbrand.com)"); return;
    }
    setSubmitting(true);
    try {
      const body = { ai_job_id: aiJobId, slug: slug || undefined, destination: dest };
      if (dest === "custom_domain") body.custom_domain = customDomain;
      const { data } = await api.post("/sites/deploy", body);
      setResult(data);
      onPublished && onPublished(data);
      toast.success("Site published 🎉");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 402) {
        toast.error("Subscribe to publish.");
        nav("/pricing?cat=ai_builder");
        onClose();
      } else { toast.error(e?.response?.data?.detail || "Publish failed"); }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="publish-modal">
      <div className="card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-white font-bold text-xl" style={{fontFamily:'Outfit'}}>Publish your site</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm" data-testid="publish-close">✕</button>
        </div>

        {allowed === false ? (
          <div className="mt-4 rounded-lg bg-amber-500/10 border border-amber-400/30 p-4 text-amber-200 text-sm" data-testid="publish-needs-sub">
            <div className="font-semibold flex items-center gap-2 mb-1"><Lock size={14}/> Subscription required</div>
            <p>To publish AI-generated sites you need an active <strong>AI Website Builder</strong> plan. Plans start at <strong>₹499/mo</strong>.</p>
            <button onClick={() => { onClose(); nav("/pricing?cat=ai_builder"); }} className="btn-primary mt-3 text-sm" data-testid="publish-go-subscribe">
              View plans →
            </button>
          </div>
        ) : result ? (
          <div className="mt-5" data-testid="publish-success">
            <div className="flex items-center gap-2 text-emerald-300 mb-3"><CheckCircle2 size={18}/> <span className="font-semibold">Your site is live.</span></div>
            <div className="space-y-2 text-sm">
              {Object.entries(result.public_urls || {}).map(([k,v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-cyan-300 hover:text-cyan-200 break-all">
                  <ExternalLink size={14}/> {v}
                </a>
              ))}
            </div>
            {dest === "custom_domain" && (
              <div className="mt-4 rounded-lg bg-slate-800 p-3 text-xs text-slate-300">
                <strong className="text-white">Next step:</strong> point <code className="text-cyan-300">{customDomain}</code> at Optivize. Add this DNS record at your registrar:
                <pre className="mt-2 p-2 bg-slate-900 rounded text-cyan-200 overflow-x-auto">Type: CNAME / A
Name: @
Value: optivize.digital (or the IP from `nslookup optivize.digital`)</pre>
              </div>
            )}
            <button onClick={onClose} className="btn-primary mt-5 w-full">Done</button>
          </div>
        ) : (
          <>
            <p className="text-slate-400 text-sm mb-4">Choose where you want to publish this AI-generated site.</p>

            <div className="grid sm:grid-cols-3 gap-2 mb-5" data-testid="publish-dest-tabs">
              {[
                { id: "subdomain",        icon: Server,      label: "Optivize subdomain", sub: "Fastest · free" },
                { id: "custom_domain",    icon: Globe,       label: "Your own domain",    sub: "Bring your DNS" },
                { id: "purchased_domain", icon: ShoppingBag, label: "Buy from us",        sub: "One-stop" },
              ].map(d => (
                <button key={d.id} onClick={() => setDest(d.id)}
                  className={`rounded-xl p-3 text-left border transition ${dest===d.id ? "border-blue-500 bg-blue-600/10" : "border-slate-700 hover:border-blue-500/60"}`}
                  data-testid={`dest-${d.id}`}>
                  <d.icon size={16} className={dest===d.id ? "text-cyan-300" : "text-slate-400"}/>
                  <div className="text-white text-sm font-semibold mt-1">{d.label}</div>
                  <div className="text-slate-500 text-xs">{d.sub}</div>
                </button>
              ))}
            </div>

            {dest === "subdomain" && (
              <div className="space-y-2">
                <label className="label">Subdomain slug</label>
                <div className="flex items-center gap-1 input p-0 overflow-hidden">
                  <input value={slug} onChange={(e)=>setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g,""))}
                    placeholder="yourbrand" className="flex-1 bg-transparent border-0 outline-none px-3 py-2 text-white" data-testid="publish-slug-input"/>
                  <span className="px-3 text-slate-400 text-sm border-l border-slate-700">.optivize.digital</span>
                </div>
                <p className="text-xs text-slate-500">3–42 chars, lowercase letters / digits / dashes.</p>
              </div>
            )}

            {dest === "custom_domain" && (
              <div className="space-y-2">
                <label className="label">Your domain</label>
                <input value={customDomain} onChange={(e)=>setCustomDomain(e.target.value.toLowerCase().trim())}
                  placeholder="yourbrand.com" className="input w-full" data-testid="publish-custom-domain"/>
                <p className="text-xs text-slate-500">After publishing, you'll get DNS instructions to point this domain at Optivize.</p>
              </div>
            )}

            {dest === "purchased_domain" && (
              <div className="space-y-3" data-testid="publish-buy-domain">
                <p className="text-sm text-slate-300">Find a domain available right now — we'll register it for you in seconds, then publish your site to it.</p>
                <DomainSearch compact />
                <p className="text-xs text-slate-500">Pricing varies by TLD. After purchase, return here to finish publishing.</p>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost !py-2 !px-4" data-testid="publish-cancel">Cancel</button>
              <button onClick={publish} disabled={submitting || dest === "purchased_domain"}
                className="btn-primary inline-flex items-center gap-2" data-testid="publish-confirm">
                {submitting ? <Loader2 size={14} className="animate-spin"/> : <Server size={14}/>}
                {dest === "purchased_domain" ? "Buy domain first" : "Publish now"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
